// src/pages/Account/AccountPaymentsPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getMyReservations } from '../../services/reservationService';
import { listDocuments, getDownloadUrl } from '../../services/documentService';
import { confirmPayment } from '../../services/stripeService';
import { useAuth } from '../../contexts/AuthContext';
import AccordionItem from '../../components/AccordionItem/AccordionItem';
import Button from '../../components/Button/Button';
import StripePayment from '../../components/StripePayment/StripePayment';
import pageStyles from './AccountPage.module.css';
import styles from './AccountPaymentsPage.module.css';

// Helper function to calculate the number of days for a reservation
const calculateDayCount = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const AccountPaymentsPage = () => {
  const [reservations, setReservations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [redirectSuccess, setRedirectSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { currentUser } = useAuth();

  const fetchData = useCallback(async () => {
    if (!currentUser) return;
    setIsLoading(true);
    setError(null);
    try {
      const [reservationsData, documentsData] = await Promise.all([
        getMyReservations(),
        listDocuments()
      ]);

      console.log('Fetched Reservations Data:', reservationsData);

      setReservations(reservationsData || []);
      setDocuments(documentsData || []);
    } catch (err) {
      setError(err.message || 'Failed to load page data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // Handle Stripe redirect return - when user comes back from redirect-based payment
  useEffect(() => {
    const handleRedirectReturn = async () => {
      const paymentIntentId = searchParams.get('payment_intent');
      const redirectStatus = searchParams.get('redirect_status');

      if (paymentIntentId && redirectStatus === 'succeeded') {
        console.log('[AccountPayments] Stripe redirect return detected, confirming payment...');
        setRedirectSuccess(true);

        // Try to confirm the payment on the backend
        // The paymentIntent metadata contains the reservationId, but we need to pass it
        // We'll extract it from the payment intent on the backend side
        try {
          // We need the reservationId - get it from the payment intent metadata
          // For now, call confirm with just the payment intent ID
          // The backend will look up the reservation from the PI metadata
          await confirmPayment(paymentIntentId, searchParams.get('reservationId') || '');
        } catch (confirmErr) {
          console.warn('Could not confirm redirect payment (webhook may handle it):', confirmErr);
        }

        // Clean up the URL params
        navigate('/account/payments', { replace: true });

        // Refetch data after a brief delay to let DB update
        setTimeout(() => {
          fetchData();
        }, 1500);
      } else {
        // Normal page load
        fetchData();
      }
    };

    handleRedirectReturn();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand accordion if reservationId is in URL
  useEffect(() => {
    const reservationIdFromUrl = searchParams.get('reservationId');
    if (reservationIdFromUrl && !isLoading) {
      const resId = parseInt(reservationIdFromUrl, 10);
      const isUnpaid = reservations.some(
        r => r.id === resId && r.status === 'pending' && r.paymentStatus === 'unpaid'
      );
      if (isUnpaid) {
        setActiveAccordion(resId);
      }
    }
  }, [searchParams, reservations, isLoading]);

  const handlePaymentSuccess = useCallback(() => {
    // Add a delay before refetching to let the backend DB update complete
    setTimeout(() => {
      fetchData();
    }, 2000);
  }, [fetchData]);

  const { unpaidReservations, paidReservations } = useMemo(() => {
    const unpaid = reservations.filter(
      (res) => res.status === 'pending' && res.paymentStatus === 'unpaid'
    ).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    const paid = reservations.filter(
      (res) => res.paymentStatus === 'paid' || res.paymentStatus === 'paid_manual'
    ).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    return { unpaidReservations: unpaid, paidReservations: paid };
  }, [reservations]);

  const findReceiptForReservation = (reservationId) => {
    const searchString = `Reservation ID: ${reservationId}`;
    return documents.find(doc =>
      doc.description && doc.description.includes(searchString)
    );
  };

  const handleDownloadReceipt = async (doc) => {
    try {
      if (!doc) throw new Error("Receipt document not found.");
      const { downloadUrl } = await getDownloadUrl(doc.id);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      setError(err.message || 'Could not download receipt.');
    }
  };

  if (isLoading) {
    return <div className={pageStyles.accountPageContainer}><p>Loading payment information...</p></div>;
  }

  if (error) {
    return <div className={pageStyles.accountPageContainer}><p className={pageStyles.errorMessage}>{error}</p></div>;
  }

  return (
    <div className={pageStyles.accountPageContainer}>
      <h2 className="page-main-title">Payments</h2>

      {redirectSuccess && (
        <div className={styles.redirectSuccessBanner}>
          <span>✓</span> Payment completed successfully! Your reservation has been updated.
        </div>
      )}

      <div className={styles.paymentsSection}>
        <h3>Pending Payments</h3>
        {unpaidReservations.length > 0 ? (
          unpaidReservations.map((res) => {
            return (
              <AccordionItem
                key={res.id}
                title={`Pay for: ${res.eventName}`}
                isOpen={activeAccordion === res.id}
                onToggle={() => setActiveAccordion(activeAccordion === res.id ? null : res.id)}
              >
                <p>
                  Please complete payment for your reservation from
                  <strong> {new Date(res.startTime).toLocaleDateString()} </strong> to
                  <strong> {new Date(res.endTime).toLocaleDateString()}</strong>.
                </p>

                <StripePayment
                  reservationId={res.id}
                  dayCount={calculateDayCount(res.startTime, res.endTime)}
                  onPaymentSuccess={handlePaymentSuccess}
                />

              </AccordionItem>
            );
          })
        ) : (
          <p>You have no pending payments.</p>
        )}
      </div>

      <div className={styles.paymentsSection}>
        <h3>Payment History</h3>
        {paidReservations.length > 0 ? (
          <ul className={styles.paidList}>
            {paidReservations.map((res) => {
              const receipt = findReceiptForReservation(res.id);
              return (
                <li key={res.id} className={styles.paidListItem}>
                  <span>
                    Payment for <strong>{res.eventName}</strong> ({new Date(res.startTime).toLocaleDateString()})
                  </span>
                  {res.paymentStatus === 'paid_manual' ? (
                    <span className={styles.adminApproved}>no receipt (approved by Admin)</span>
                  ) : receipt ? (
                    <Button size="sm" variant="secondary" onClick={() => handleDownloadReceipt(receipt)}>
                      View Receipt
                    </Button>
                  ) : (
                    <span className={styles.receiptUnavailable}>Receipt unavailable</span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p>No payment history found.</p>
        )}
      </div>
    </div>
  );
};

export default AccountPaymentsPage;